import { motion } from "framer-motion";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { HBLoadingSpinnerOnly } from "~components";
import { Css, Xss } from "~generated/css";
import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";
import { useTestIds } from "~utils";
import { FloorPlanOptionGroup_CheckoutOptionGroupFragment } from "./FloorPlan.generated";

// In-memory cache for SVG content to avoid re-fetching the same floor plan
const svgCache = new Map<string, Promise<string>>();

/**
 * Can be called early (e.g., when images are received) to preload and improve load times.
 * We do this because our SVGs are stored as assets, not raw text or images so we need to handle it ourselves so we're not waiting to fetch
 * them when we're in a model home with bad internet and not refetching them every time the floor plan is rendered.
 */
export function fetchAndCacheSVG(src: string): Promise<string> {
  if (!src) {
    return Promise.resolve("");
  }

  // Return cached promise if available
  const cached = svgCache.get(src);
  if (cached) {
    return cached;
  }

  // Create new fetch promise
  const fetchPromise = (async () => {
    try {
      const response = await fetch(src);
      if (!response.ok) {
        throw new Error(`Failed to fetch SVG: ${response.statusText}`);
      }
      return response.text();
    } catch (error) {
      // Remove failed cache entry so it can be retried
      svgCache.delete(src);
      throw error;
    }
  })();

  svgCache.set(src, fetchPromise);
  return fetchPromise;
}

type FloorPlanProps = {
  // URL to a .svg file representing the floor plan
  src: string;
  // Allow for styles to be applied to the container
  xss?: Xss<"height" | "width">;
  // Allow override of highlight color
  highlightHexColor?: string;
  // Allow override of mask color
  maskHexColor?: string;
  // Specific options to show geometry for
  selectedOptionCodes?: string[];
  // Specific options to highlight
  highlightedOptionCodes?: string[];
  // Whether to reverse the floor plan
  isReversed?: boolean;
  // Used to scope styles
  id: string;
  // Whether to reduce the amount of effective padding when shown in a small "preview" card
  isPreview?: boolean;
};

export const FloorPlan = (props: FloorPlanProps) => {
  const {
    src,
    xss,
    selectedOptionCodes = [],
    highlightedOptionCodes = [],
    highlightHexColor = "#FFFF00",
    maskHexColor = "#FFFFFF",
    isReversed = false,
    isPreview = false,
    id,
  } = props;

  const tid = useTestIds(props, "floorPlan");

  // stores the SVG text that will be injected into the page via `dangerouslySetInnerHTML`
  const [svgText, setSvgText] = useState<string>("");
  // stores the reference to the `div` containing the `svg` element
  const svgContainerRef = useRef<HTMLDivElement>(null);
  // internal loading state
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Reset states when src changes
    setIsLoading(true);
    setSvgText("");

    // Fetch the .svg file content and inject into page via state.
    // We do this since using `<img />`, `<object />` or `<iframe />`
    // does not offer the ability to customize its styles
    void (async () => {
      try {
        // NOTE: This right here is why `Asset.downloadUrl` works. We're fetching it
        // explicitly here.
        const svgText = await fetchAndCacheSVG(src);
        setSvgText(svgText);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load floor plan SVG:", error);
        setIsLoading(false);
      }
    })();
  }, [src]);

  useLayoutEffect(() => {
    if (svgContainerRef.current && svgText && !isLoading) {
      const svg = svgContainerRef.current.querySelector("svg");
      if (svg) {
        try {
          resetFloorPlan(svg);
          hideMetaElements(svg);
          showOptions(svg, selectedOptionCodes, isReversed);
        } catch (error) {
          console.error("Floor plan render error", error);
        }
      }
    }
  }, [highlightedOptionCodes, isReversed, maskHexColor, selectedOptionCodes, svgText, isLoading]);

  return (
    <div css={Css.relative.h100.w100.$}>
      {/* Loading spinner mask */}
      <motion.div
        initial={{ opacity: 1 }}
        css={Css.absolute.bgOffWhite.z1.w100.h100.$}
        animate={
          !isLoading && {
            opacity: 0,
            transition: { duration: 0.4 },
            transitionEnd: { display: "none", zIndex: -1 },
          }
        }
      >
        <div css={Css.absolute.top("50%").left("50%").add("transform", "translate(-50%, -50%)").$}>
          <HBLoadingSpinnerOnly />
        </div>
      </motion.div>

      {/* Floor plan content */}
      <div
        css={{
          ...xss,
          svg: {
            // Zoom and recenter the floor plan this is based on Strand's plans
            height: "125%",
            width: "125%",
            marginLeft: "-12%",
            marginTop: isPreview ? "-5%" : "-12%",
          },
        }}
        ref={svgContainerRef}
        dangerouslySetInnerHTML={{ __html: svgText }}
        {...tid}
      />
    </div>
  );
};

function resetFloorPlan(svg: SVGSVGElement) {
  const floorPlanFlip = svg.querySelector<SVGGElement>(`g[id="FLOORPLAN_FLIP"]`);
  const dimensionsFlip = svg.querySelector<SVGGElement>(`g[id="DIMENSIONS_FLIP"]`);
  const roomNamesFlip = svg.querySelector<SVGGElement>(`g[id="ROOMNAMES_FLIP"]`);
  floorPlanFlip && (floorPlanFlip.style.display = "none");
  dimensionsFlip && (dimensionsFlip.style.display = "none");
  roomNamesFlip && (roomNamesFlip.style.display = "none");

  const allOptionLayers = Array.from(svg.querySelectorAll<SVGGElement>(`g[id*="OPT_CODE_"]`));
  // Than this is a STRAND floor plan
  if (allOptionLayers.nonEmpty) {
    allOptionLayers.forEach((layer) => {
      layer.style.display = "none";
    });
  } else {
    // Then This is an internally created HB floor plan and we need to search for `OPTION-CODE-`
    const hbAllOptionLayers = Array.from(svg.querySelectorAll<SVGGElement>(`g[id*="OPTION-CODE-"]`));
    hbAllOptionLayers.forEach((layer) => {
      layer.style.display = "none";
    });
  }
}

// Hide anything that's not the floor plan
// NOTE: Right now this still catches the floor level text. We need that separated out
function hideMetaElements(svg: SVGSVGElement) {
  const planText = svg.querySelectorAll<SVGGElement>(`g[id*="PLAN_TEXT"]`)?.[0];
  const logo = svg.querySelectorAll<SVGGElement>(`g[id*="LOGO"]`)?.[0];
  planText && (planText.style.display = "none");
  logo && (logo.style.display = "none");
}

function showOptions(svg: SVGSVGElement, selectedOptionCodes: string[], isReversed: boolean) {
  // or isDallasInfillPlan
  const isStrandPlan =
    !!svg.querySelector<SVGGElement>(`g[id*="OPT_CODE_"]`) ||
    !!svg.querySelector<SVGGElement>(`g[id*="FLOORPLAN_CAD"]`);

  if (isStrandPlan) {
    const floorPlanStandard = svg.querySelector<SVGGElement>(`g[id="FLOORPLAN_STANDARD"]`);
    const floorPlanFlip = svg.querySelector<SVGGElement>(`g[id="FLOORPLAN_FLIP"]`);
    const dimensionsStandard = svg.querySelector<SVGGElement>(`g[id="DIMENSIONS_STANDARD"]`);
    const dimensionsFlip = svg.querySelector<SVGGElement>(`g[id="DIMENSIONS_FLIP"]`);
    const roomNamesStandard = svg.querySelector<SVGGElement>(`g[id="ROOMNAMES_STANDARD"]`);
    const roomNamesFlip = svg.querySelector<SVGGElement>(`g[id="ROOMNAMES_FLIP"]`);

    // Lakeside strand plans require us to TOGGLE layers
    // whereas the earlier strand plans (Dallas Infill) layered the flips on top of standard so it wasnt required
    // We have communicated this with design in hopes of updating older plans and then going forward
    // we will always toggle.
    floorPlanFlip && (floorPlanFlip.style.display = isReversed ? "inline" : "none");
    floorPlanStandard && (floorPlanStandard.style.display = isReversed ? "none" : "inline");
    dimensionsFlip && (dimensionsFlip.style.display = isReversed ? "inline" : "none");
    roomNamesFlip && (roomNamesFlip.style.display = isReversed ? "inline" : "none");
    dimensionsStandard && (dimensionsStandard.style.display = isReversed ? "none" : "inline");
    roomNamesStandard && (roomNamesStandard.style.display = isReversed ? "none" : "inline");
    for (const selectedOptionCode of selectedOptionCodes) {
      const optionCodeId = `OPT_CODE_${selectedOptionCode}`;
      const optionCodeFlipId = `OPT_CODE_${selectedOptionCode}_FLIP`;
      const optionLayer = svg.querySelector<SVGGElement>(`g[id="${optionCodeId}"]`);
      const optionLayerFlip = svg.querySelector<SVGGElement>(`g[id="${optionCodeFlipId}"]`);
      if (optionLayer) {
        optionLayer.style.display = !isReversed ? "inline" : "none";
        optionLayerFlip && (optionLayerFlip.style.display = isReversed ? "inline" : "none");
      }
    }
  } else {
    // Eaton Fire community
    // These HB created floor plans use `OPTION-CODE-` instead of `OPT_CODE-`, dont have flips, and dont have any text
    // to manipulate.
    // They're very incomplete and a rush job we'll need to iterate on as they get updated until they're replaced by Strand plans.
    // we do the same as above but without readable flips and text manipulation.
    svg.style.transform = isReversed ? "scaleX(-1)" : "scaleX(1)";
    for (const selectedOptionCode of selectedOptionCodes) {
      const optionCodeId = `OPTION-CODE-${selectedOptionCode}`;
      // Note, we're using a wildcard to match the option code id since we're rushing and not going to manually update ids
      const optionLayer = svg.querySelector<SVGGElement>(`g[id*="${optionCodeId}"]`);
      if (optionLayer) {
        const isHidden = optionLayer.style.display === "none";
        optionLayer.style.display = isHidden ? "inline" : "none";
      }
    }
  }
}

export function getSelectedStructuralOptionCodes(
  optionGroups: FloorPlanOptionGroup_CheckoutOptionGroupFragment[],
  selectedOptionIds: string[],
) {
  return optionGroups
    .filter(
      (ccgo) =>
        ccgo.step === CheckoutConfigOptionGroupStep.OptionsStructural ||
        // LA Eaton fire plan elevations contribute to the floor plan
        ccgo.step === CheckoutConfigOptionGroupStep.ExteriorElevation,
    )
    .flatMap((ccog) => ccog.options)
    .filter((cco) => selectedOptionIds.includes(cco.id))
    .map((option) => option.option.code);
}
